import { Injectable } from "@angular/core";
import { UserService } from "@main-services/user.service";
import { Observable } from "rxjs";
import { StakingStateService } from "@modules/staking/domain/+state/staking.state.service";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { skipWhile, take, tap } from "rxjs/operators";
import { StakingProcess } from "@modules/staking/domain/entities/staking-process.model";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StakingRemote } from "@modules/staking/domain/infrastructure/staking.remote";
import {
	GetStakingInfoQuery,
	StakeMutation,
	StakingAction,
	StakingActionInput,
	UnstakeMutation,
	GetStakingRewardsQuery,
	GetStakingFilteredAssetsQuery
} from "@gql/staking";
import { StakingState } from "@modules/staking/domain/entities/staking-state.model";
import { StakingFlowStep } from "@modules/shared/domain/entities/trade-flow.model";
import { StakingHelperService } from "@modules/staking/domain/+state/utility/staking-helper.service";
import { ServiceNames } from "@models/service_enums";
import { InitService } from "@core-services/init.service";
import { EXCLUDED_ASSET_SYMBOLS } from "@helpers/constants";

@Injectable({
	providedIn: "root"
})
export class StakingFacade {
	public constructor(
		private readonly stakingStateService: StakingStateService,
		private readonly userService: UserService,
		private readonly stakingRemote: StakingRemote,
		private readonly stakingHelperService: StakingHelperService,
		private readonly initService: InitService
	) {
		this.initService
			.waitForInitServices([ServiceNames.USER])
			.pipe(
				take(1),
				tap(() => this.checkUser())
			)
			.subscribe();
	}

	private checkUser(): void {
		if (this.userService.user.isVerified()) {
			this.fetchStakingInfo();
		}
	}

	public updateStakingRewards(): void {
		this.getStakeables$()
			.pipe(
				skipWhile((stakeables) => stakeables.length === 0),
				take(1)
			)
			.subscribe((stakeables: Stakeable[]) => {
				this.stakingRemote
					.fetchStakingRewards()
					.pipe(take(1))
					.subscribe((stakingRewards: GetStakingRewardsQuery) => {
						const updatedItems: Stakeable[] = stakeables.map((stakeable) =>
							this.stakingHelperService.updateStakeable(stakeable, stakingRewards)
						);
						this.stakingStateService.updateStakeables(updatedItems);
					});
			});
	}

	public fetchStakingInfo(): void {
		this.stakingRemote
			.fetchStakingInfo()
			.pipe(take(1))
			.subscribe((stakingInfo: GetStakingInfoQuery) => {
				const stakingEnabled: boolean =
					stakingInfo.assetConfig.stakingEnabled && stakingInfo.assetConfig.enabledForUserCountry;
				this.stakingStateService.updateStakingState({
					isStakingEnabled: stakingEnabled && this.userService.user.isVerified()
				});
				this.stakingStateService.updateStakingProcess({ showPreScreen: !stakingInfo.hasHistory });
			});
	}

	public updateFilteredAssets(searchInput: string, filter: string): void {
		this.stakingStateService.updateStakingProcess({ isLoading: true });
		this.stakingRemote
			.fetchFilteredAssets(searchInput, filter)
			.pipe()
			.pipe(take(1))
			.subscribe(
				(filteredAssets: GetStakingFilteredAssetsQuery) => {
					const updatedList: Stakeable[] = this.stakingHelperService.getParsedStakeables(filteredAssets);
					const filteredList = updatedList.filter((f) => {
						return !EXCLUDED_ASSET_SYMBOLS.includes(f.symbol);
					});
					this.stakingStateService.updateStakeables(filteredList);
					this.stakingStateService.updateStakingProcess({ isLoading: false });
				},
				() => this.stakingStateService.updateStakingProcess({ error: true })
			);
	}

	public setWalletByTradableId(id: string): void {
		const wallet: TradableWallet = this.stakingHelperService
			.getWalletsByStakeableId(id)
			.find((wallet: TradableWallet) => wallet.isDefault());
		this.updateStakingProcess({ wallet });
	}

	// ------------> Stake and Unstake Mutations

	public stake(stakeables: Stakeable[], process: StakingProcess): void {
		const stakeAction: StakingActionInput = this.stakingHelperService.getStakeActionInput(process);
		this.stakingRemote
			.stake(stakeAction)
			.pipe(take(1))
			.subscribe((res: StakeMutation) => {
				if (res.stake.__typename === "StakingAction") {
					const successfulStake: StakingAction = res.stake as StakingAction;
					this.updateStakedBalance(successfulStake, stakeables);
					this.goToStep(StakingFlowStep.SUCCESS);
				}
				if (res.stake.__typename === "StakingActionInProgress") {
					this.goToStep(StakingFlowStep.PENDING);
				}
				this.updateStakingProcess({ showPreScreen: false });
			});
	}

	public unstake(stakeables: Stakeable[], process: StakingProcess): void {
		const stakeAction: StakingActionInput = this.stakingHelperService.getStakeActionInput(process);
		this.stakingRemote
			.unstake(stakeAction)
			.pipe(take(1))
			.subscribe((res: UnstakeMutation) => {
				if (res.unstake.__typename === "StakingAction") {
					const successfulUnstake: StakingAction = res.unstake as StakingAction;
					this.updateStakedBalance(successfulUnstake, stakeables);
					this.goToStep(StakingFlowStep.SUCCESS);
				}
				if (res.unstake.__typename === "StakingActionInProgress") {
					this.goToStep(StakingFlowStep.PENDING);
				}
			});
	}

	private updateStakedBalance(stakeData: StakingAction, stakeables: Stakeable[]): void {
		const stakedAssetId: string = stakeData?.asset.assetId;
		const updatedItems: Stakeable[] = stakeables.map((item: Stakeable) =>
			item.id === stakedAssetId
				? {
						...item,
						stakedAmount: stakeData.balanceAfter.value
					}
				: item
		);
		this.stakingStateService.updateStakeables(updatedItems);
		const stakedWalletId: string = stakeData?.walletId;
		if (stakedWalletId) {
			this.stakingRemote
				.refetchDefaultWallet(stakedWalletId, this.stakingHelperService.getWalletService(stakedWalletId))
				.pipe(take(1))
				.subscribe((wallet: TradableWallet) =>
					this.stakingHelperService.getWalletService(wallet.getId()).updateItem(wallet)
				);
		}
	}

	// ------------> Staking-Related Utilities and Getters

	public isStakingEnabled$(): Observable<boolean> {
		return this.stakingStateService.isStakingEnabled$();
	}

	public getErrorState$(): Observable<boolean> {
		return this.stakingStateService.getErrorState$();
	}

	public getLoadingState$(): Observable<boolean> {
		return this.stakingStateService.getLoadingState$();
	}

	public getAvailableStakeables$(): Observable<Stakeable[]> {
		return this.stakingStateService.getAvailableStakeables$();
	}

	public getStakingProcess$(): Observable<StakingProcess> {
		return this.stakingStateService.getStakingProcess$();
	}

	public getStakingState$(): Observable<StakingState> {
		return this.stakingStateService.getStakingState$();
	}

	public getStakeables$(): Observable<Stakeable[]> {
		return this.stakingStateService.getStakeables$();
	}

	public getStakedAssets$(): Observable<Stakeable[]> {
		return this.stakingStateService.getStakedAssets$();
	}

	public goToStep(step: StakingFlowStep): void {
		this.stakingStateService.goToStep(step);
	}

	public updateStakingProcess(process: Partial<StakingProcess>): void {
		this.stakingStateService.updateStakingProcess(process);
	}

	public resetStakingProcess(): void {
		this.stakingStateService.resetStakingProcess();
	}

	public resetStakingProcessAmount(): void {
		this.stakingStateService.resetStakingProcessAmount();
	}
}
